/**
 * マッピング設定 Excel ダウンロード: ExcelJS によるファイル生成とダウンロード。
 *
 * 実装計画 6.1 / 6.2 に対応する。
 *
 * - 1 シート（マッピング設定）
 * - 1〜3 行目: 表タイトル、4 行目: ヘッダ（ウィンドウ枠固定・オートフィルタ）
 * - セル結合は行わない（AutoFilter / ピボットをそのまま使えるようにする）
 */
import ExcelJS from 'exceljs'

import { EXPORT_STATUS } from './mappingExportModel'

/** シート名 */
const SHEET_NAME = 'マッピング設定'

/** ヘッダ行のラベル（Excel A〜K 列） */
const HEADERS = [
  'データカテゴリ',
  'データ集類',
  '項目名',
  'データ型',
  'ステータス',
  'マッピング方法',
  '優先度',
  '集計方法',
  'ファイル種別',
  'カラム名',
  'マッピング内容'
]

/** 列幅（文字単位） */
const COLUMN_WIDTHS = [18, 16, 26, 10, 16, 24, 8, 10, 16, 16, 62]

/** 中央寄せにする列番号（1 始まり）: データ型・ステータス・優先度・集計方法 */
const CENTERED_COLUMNS = new Set([4, 5, 7, 8])

const TITLE_ROW = 1
const SUBTITLE_ROW = 2
const HEADER_ROW = 4

const FONT_NAME = 'Yu Gothic'

const HEAD_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE1E6ED' } }
const THIN_BORDER = { style: 'thin', color: { argb: 'FFBFC5D2' } }
const BORDER = { top: THIN_BORDER, bottom: THIN_BORDER, left: THIN_BORDER, right: THIN_BORDER }

/** ステータス別の背景色。注意が必要な 2 ステータスのみ着色する（実装計画 6.3）。 判定ルール適用・設定済は着色しない。 */
const STATUS_FILLS = {
  [EXPORT_STATUS.UNSET]: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF3CD' } },
  [EXPORT_STATUS.REMOVED]: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F3F5' } }
}

/** ファイル名に使えない文字（Windows）。含まれるとダウンロードが無言で失敗する */
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]/g

/**
 * 期間ラベルをファイル名に使える形へサニタイズする。
 *
 * `period_label` は DynamoDB の `PeriodValue` 由来のため、`/` 等が含まれる可能性がある。
 *
 * @param {unknown} periodLabel
 * @returns {string}
 */
export function sanitizeFileNamePart(periodLabel) {
  return String(periodLabel ?? '')
    .trim()
    .replace(INVALID_FILENAME_CHARS, '-')
}

/**
 * ダウンロード時のファイル名を組み立てる（実装計画 6.1.2）
 *
 * @param {unknown} periodLabel - IF02095 の period_label
 * @returns {string}
 */
export function buildExportFileName(periodLabel) {
  const safeLabel = sanitizeFileNamePart(periodLabel)
  return safeLabel === '' ? 'マッピング設定一覧.xlsx' : `マッピング設定一覧_${safeLabel}.xlsx`
}

/**
 * 表タイトル（1 行目）の文字列を組み立てる
 *
 * @param {{ periodTypeLabel?: string; periodLabel?: string }} taskInfo
 * @returns {string}
 */
function buildTitleText(taskInfo) {
  const periodTypeLabel = String(taskInfo?.periodTypeLabel ?? '').trim()
  const periodLabel = String(taskInfo?.periodLabel ?? '').trim()
  if (periodTypeLabel === '' && periodLabel === '') return 'マッピング設定一覧'
  if (periodTypeLabel === '') return `マッピング設定一覧（${periodLabel}）`
  if (periodLabel === '') return `マッピング設定一覧（${periodTypeLabel}）`
  return `マッピング設定一覧（${periodTypeLabel}：${periodLabel}）`
}

/**
 * サブタイトル（2 行目）の文字列を組み立てる
 *
 * @param {{ periodTypeLabel?: string; periodLabel?: string; exportedAt?: string }} taskInfo
 * @returns {string}
 */
function buildSubtitleText(taskInfo) {
  // 種類・期間は 1 行目のタイトルに含まれるため、2 行目は出力日時のみとする
  const exportedAt = String(taskInfo?.exportedAt ?? '').trim()
  return exportedAt === '' ? '' : `出力日時：${exportedAt}`
}

/**
 * 行モデルを Excel のセル配列へ変換する
 *
 * @param {import('./mappingExportModel').MappingExportRow} row
 * @returns {(string | number)[]}
 */
function toCellValues(row) {
  return [
    row.categoryName,
    row.dataKindName,
    row.columnName,
    row.dataType,
    row.status,
    row.mappingMethod,
    row.priority,
    row.numAggType,
    row.fileType,
    row.sourceColumnName,
    row.content
  ]
}

/**
 * タイトル行・サブタイトル行を書き込む
 *
 * @param {ExcelJS.Worksheet} sheet
 * @param {{ periodTypeLabel?: string; periodLabel?: string; exportedAt?: string }} taskInfo
 */
function writeTitleRows(sheet, taskInfo) {
  sheet.mergeCells(TITLE_ROW, 1, TITLE_ROW, HEADERS.length)
  const titleCell = sheet.getCell(TITLE_ROW, 1)
  titleCell.value = buildTitleText(taskInfo)
  titleCell.font = { name: FONT_NAME, size: 14, bold: true, color: { argb: 'FF1F3864' } }
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' }
  sheet.getRow(TITLE_ROW).height = 26

  sheet.mergeCells(SUBTITLE_ROW, 1, SUBTITLE_ROW, HEADERS.length)
  const subtitleCell = sheet.getCell(SUBTITLE_ROW, 1)
  subtitleCell.value = buildSubtitleText(taskInfo)
  subtitleCell.font = { name: FONT_NAME, size: 10, color: { argb: 'FF595959' } }
  subtitleCell.alignment = { vertical: 'middle', horizontal: 'left' }
  sheet.getRow(SUBTITLE_ROW).height = 18
  // 3 行目は余白として空行のままにする
}

/**
 * ヘッダ行を書き込む
 *
 * @param {ExcelJS.Worksheet} sheet
 */
function writeHeaderRow(sheet) {
  const headerRow = sheet.getRow(HEADER_ROW)
  headerRow.values = HEADERS
  headerRow.height = 24
  headerRow.eachCell((cell) => {
    cell.font = { name: FONT_NAME, size: 11, bold: true }
    cell.fill = HEAD_FILL
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
    cell.border = BORDER
  })
}

/**
 * データ行を書き込む
 *
 * @param {ExcelJS.Worksheet} sheet
 * @param {import('./mappingExportModel').MappingExportRow[]} rows
 */
function writeDataRows(sheet, rows) {
  for (const row of rows) {
    const excelRow = sheet.addRow(toCellValues(row))
    const fill = STATUS_FILLS[row.status]
    excelRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { name: FONT_NAME, size: 10 }
      cell.border = BORDER
      cell.alignment = {
        vertical: 'top',
        horizontal: CENTERED_COLUMNS.has(colNumber) ? 'center' : 'left',
        wrapText: true
      }
      if (fill) cell.fill = fill
    })
  }
}

/**
 * ワークブックを生成する（ダウンロードは行わない。テストから検証しやすくするため分離）
 *
 * @param {import('./mappingExportModel').MappingExportRow[]} rows
 * @param {{ periodTypeLabel?: string; periodLabel?: string; exportedAt?: string }} taskInfo
 * @returns {ExcelJS.Workbook}
 */
export function buildMappingWorkbook(rows, taskInfo = {}) {
  const dataRows = rows ?? []
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'CHROFY'

  const sheet = workbook.addWorksheet(SHEET_NAME, {
    views: [{ state: 'frozen', ySplit: HEADER_ROW }]
  })

  COLUMN_WIDTHS.forEach((width, index) => {
    sheet.getColumn(index + 1).width = width
  })

  writeTitleRows(sheet, taskInfo)
  writeHeaderRow(sheet)
  writeDataRows(sheet, dataRows)

  sheet.autoFilter = {
    from: { row: HEADER_ROW, column: 1 },
    to: { row: HEADER_ROW + dataRows.length, column: HEADERS.length }
  }

  return workbook
}

/**
 * Blob をブラウザでダウンロードさせる
 *
 * @param {Blob} blob
 * @param {string} fileName
 */
function triggerDownload(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * マッピング設定 Excel を生成してダウンロードする。
 *
 * @param {import('./mappingExportModel').MappingExportRow[]} rows
 * @param {{ periodTypeLabel?: string; periodLabel?: string; exportedAt?: string }} taskInfo
 * @returns {Promise<void>}
 */
export async function exportMappingToExcel(rows, taskInfo = {}) {
  const workbook = buildMappingWorkbook(rows, taskInfo)
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  triggerDownload(blob, buildExportFileName(taskInfo?.periodLabel))
}
